/**
 * Главная страница без входа (ГЛ-01): что делает портал, для кого, разделы со снимками экранов, как устроено,
 * тарифы, частые вопросы, контакты; кнопки «Войти», «Попробовать демо», «Запросить доступ». Три языка (НФ-16),
 * открывается с телефона. Снимки — демо-компания, лежат в /landing/*.webp.
 */
import { MODULES, PLAN_MODULES, type PublicPlan, type SiteInfo } from '@epc/contracts';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ArrowRight, Mail, Phone } from 'lucide-react';
import { useEffect, useState } from 'react';
import { DemoEntry } from '../../components/Demo';
import { UpdateBanner } from '../../components/UpdateBanner';
import { Logo, LogoMark } from '../../layout/Logo';
import { api } from '../../lib/api';
import { captureAttribution } from '../../lib/attribution';
import { meQuery } from '../../lib/session';
import { CookieBanner } from './CookieBanner';
import { DICT, detectLang, LANGS, type Lang, rememberLang } from './i18n';
import { SignupForm } from './SignupForm';

const siteQuery = {
  queryKey: ['public', 'site'],
  queryFn: () => api<SiteInfo>('/public/site'),
  staleTime: 300_000,
};
const plansQuery = {
  queryKey: ['public', 'plans'],
  queryFn: () => api<PublicPlan[]>('/public/plans'),
  staleTime: 300_000,
};

const btnBase =
  'inline-flex h-12 shrink-0 items-center justify-center gap-2 rounded-md px-5 text-[15px] font-semibold whitespace-nowrap transition-colors';
const btnPrimary = `${btnBase} bg-accent text-on-accent hover:underline underline-offset-[3px]`;
const btnSecondaryDark = `${btnBase} border border-white/40 text-white hover:border-white`;
const btnSecondary = `${btnBase} border border-line-strong bg-surface text-ink hover:underline underline-offset-[3px]`;

function money(v: number, currency: string, lang: Lang): string {
  const locale = lang === 'en' ? 'en-US' : lang === 'kk' ? 'kk-KZ' : 'ru-RU';
  return `${v.toLocaleString(locale, { maximumFractionDigits: 0 })} ${currency === 'KZT' ? '₸' : currency}`;
}

/** Снимок экрана. Рядом всегда есть подпись или заголовок, поэтому сам снимок для читалок — украшение (alt пустой). */
function Screenshot({
  name,
  dark = false,
  eager = false,
}: {
  name: string;
  dark?: boolean;
  eager?: boolean;
}) {
  return (
    <figure className={clsx('visor relative m-0 overflow-hidden rounded-lg', dark && 'visor-on-dark')}>
      <img
        src={`/landing/${name}.webp`}
        alt=""
        width={1200}
        height={750}
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : 'auto'}
        decoding="async"
        className="block h-auto w-full border border-line bg-surface"
      />
    </figure>
  );
}

function LangSwitch({ lang, onChange }: { lang: Lang; onChange: (l: Lang) => void }) {
  return (
    <fieldset className="inline-flex h-12 items-center rounded-md border border-line p-0.5 md:h-11">
      <legend className="sr-only">{DICT[lang].lang}</legend>
      {LANGS.map((l) => (
        <button
          key={l.code}
          type="button"
          lang={l.code}
          aria-pressed={lang === l.code}
          aria-label={l.title}
          onClick={() => onChange(l.code)}
          title={l.title}
          className={clsx(
            'h-11 min-w-11 rounded-[4px] px-2.5 text-[13px] font-medium md:h-10',
            lang === l.code ? 'bg-ink text-on-ink' : 'text-ink-2 hover:text-ink',
          )}
        >
          {l.short}
        </button>
      ))}
    </fieldset>
  );
}

export function LandingPage() {
  const [lang, setLang] = useState<Lang>(detectLang);
  const t = DICT[lang];
  const site = useQuery(siteQuery);
  const plans = useQuery(plansQuery);
  const me = useQuery(meQuery);
  const loggedIn = Boolean(me.data);
  // Вошедшему сотруднику настоящей компании демо и заявка на доступ ни к чему; демо-пользователю — можно
  const guest = !loggedIn || Boolean(me.data?.user.demoRole);
  // Русские фрагменты (названия тарифов и разделов — из общих справочников) помечаются языком для читалок
  const ru = lang === 'ru' ? undefined : 'ru';

  // Метки ссылки (utm, партнёр, страна) запоминаются при открытии — заявка и регистрация отправят их
  useEffect(() => {
    captureAttribution();
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
    document.title = `EPC Pilot AI — ${t.hero.title}`;
    return () => {
      document.documentElement.lang = 'ru';
      document.title = 'EPC Pilot AI';
    };
  }, [lang, t.hero.title]);

  function pick(l: Lang) {
    setLang(l);
    rememberLang(l);
  }

  const demoEnabled = site.data?.demoEnabled ?? false;
  // Пока сведения о площадке не пришли, место главной кнопки не прыгает: заглушка той же высоты
  const siteReady = !site.isPending;
  const contact = site.data?.contact ?? null;
  const contactIsMail = Boolean(contact?.includes('@'));
  const contactHref = contact
    ? contactIsMail
      ? `mailto:${contact}`
      : `tel:${contact.replace(/[^\d+]/g, '')}`
    : null;
  const showSignup = guest && Boolean(site.data?.signupEnabled);
  const navItems: Array<readonly [string, string]> = [
    ['#sections', t.nav.sections],
    ['#how', t.nav.how],
    ['#plans', t.nav.plans],
    ['#faq', t.nav.faq],
    ...(showSignup ? ([['#signup', t.signup.nav]] as const) : []),
    ['#contacts', t.nav.contacts],
  ];
  const moduleTitle = (code: string) => MODULES.find((m) => m.code === code)?.title ?? code;

  return (
    <div className="min-h-dvh bg-bg text-ink">
      <UpdateBanner variant="auth" />
      {/* Шапка */}
      <header className="sticky top-0 z-30 border-b border-line bg-surface/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1180px] items-center gap-3 px-4 py-2.5 md:px-6">
          <a href="#top" className="flex h-11 shrink-0 items-center" aria-label="EPC Pilot AI">
            <Logo height={22} className="hidden text-ink sm:block" />
            <LogoMark size={26} className="text-ink sm:hidden" />
          </a>
          <nav className="ml-6 hidden items-center gap-1 lg:flex" aria-label={t.nav.aria}>
            {navItems.map(([href, label]) => (
              <a
                key={href}
                href={href}
                className="inline-flex h-11 items-center rounded-md px-3 text-[14px] text-ink-2 hover:text-ink"
              >
                {label}
              </a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <LangSwitch lang={lang} onChange={pick} />
            {loggedIn ? (
              <Link to="/" className={clsx(btnSecondary, 'h-11 px-4 text-[14px]')}>
                {t.actions.toPortal}
              </Link>
            ) : (
              <Link to="/login" className={clsx(btnSecondary, 'h-11 px-4 text-[14px]')}>
                {t.actions.login}
              </Link>
            )}
            {guest ? (
              <span className="hidden md:inline-flex">
                <Link to="/request-access" className={clsx(btnPrimary, 'h-11 px-4 text-[14px]')}>
                  {t.actions.request}
                </Link>
              </span>
            ) : null}
          </div>
        </div>
        <nav
          className="mx-auto flex max-w-[1180px] gap-1 overflow-x-auto px-4 pb-1 md:px-6 lg:hidden"
          aria-label={t.nav.aria}
        >
          {navItems.map(([href, label]) => (
            <a
              key={href}
              href={href}
              className="inline-flex h-11 shrink-0 items-center rounded-md px-3 text-[14px] whitespace-nowrap text-ink-2 hover:text-ink"
            >
              {label}
            </a>
          ))}
        </nav>
      </header>

      {/* Первый экран */}
      <section id="top" className="bg-nav-bg text-white">
        <div className="mx-auto grid max-w-[1180px] gap-10 px-4 py-14 md:px-6 md:py-20 lg:grid-cols-[minmax(0,5fr)_minmax(0,6fr)] lg:items-center">
          <div>
            <p className="flex items-center gap-2 text-[14px] font-medium text-nav-muted">
              <span className="inline-block size-2 rounded-full bg-accent" aria-hidden />
              {t.hero.slogan}
            </p>
            <h1 className="mt-4 text-[34px] leading-[40px] font-semibold tracking-[-0.015em] md:text-[44px] md:leading-[50px]">
              {t.hero.title}
            </h1>
            <p className="mt-5 max-w-[560px] text-[16px] leading-6 text-nav-ink md:text-[17px] md:leading-7">
              {t.hero.text}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {guest && !siteReady ? <span className="inline-block h-12 w-44" aria-hidden /> : null}
              {guest && siteReady && demoEnabled ? (
                <DemoEntry label={t.actions.demo} variant="primary" size="lg" hint={false} />
              ) : null}
              {guest && siteReady && !demoEnabled ? (
                <Link to="/request-access" className={btnPrimary}>
                  {t.actions.request}
                </Link>
              ) : null}
              {loggedIn ? (
                <Link to="/" className={clsx(guest ? btnSecondaryDark : btnPrimary)}>
                  {t.actions.toPortal}
                </Link>
              ) : (
                <Link to="/login" className={btnSecondaryDark}>
                  {t.actions.login}
                </Link>
              )}
              {guest && siteReady && demoEnabled ? (
                <Link to="/request-access" className={clsx(btnSecondaryDark, 'md:hidden')}>
                  {t.actions.request}
                </Link>
              ) : null}
            </div>
            <ul className="mt-10 grid gap-4 border-t border-white/15 pt-6 text-[14px] leading-5 text-nav-ink sm:grid-cols-3">
              {t.hero.facts.map((f) => (
                <li key={f} className="flex gap-2.5">
                  <LogoMark size={18} className="mt-0.5 shrink-0 text-white/70" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <Screenshot name="portfolio" dark eager />
            <p className="mt-3 text-[13px] text-nav-muted">{t.hero.shotCaption}</p>
          </div>
        </div>
      </section>

      {/* Для кого */}
      <section className="border-b border-line bg-surface">
        <div className="mx-auto max-w-[1180px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
            {t.forWhom.title}
          </h2>
          <dl className="mt-8 grid gap-x-10 gap-y-7 sm:grid-cols-2 lg:grid-cols-3">
            {t.forWhom.items.map((c) => (
              <div key={c.title} className="border-t border-line pt-4">
                <dt className="text-[16px] font-semibold text-ink">{c.title}</dt>
                <dd className="mt-1.5 text-[14px] leading-5 text-ink-2">{c.text}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Разделы со снимками */}
      <section id="sections" className="scroll-mt-20">
        <div className="mx-auto max-w-[1180px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
            {t.sections.title}
          </h2>
          <p className="mt-2 text-[14px] text-ink-2">{t.sections.text}</p>
          <ol className="mt-10 flex flex-col gap-14">
            {t.sections.items.map((s, i) => (
              <li
                key={s.image}
                className={clsx(
                  'grid items-center gap-6 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] lg:gap-12',
                  i % 2 === 1 && 'lg:[&>div:first-child]:order-2',
                )}
              >
                <div>
                  <h3 className="text-[20px] leading-7 font-semibold text-ink">{s.title}</h3>
                  <p className="mt-2 text-[15px] leading-6 text-ink-2">{s.text}</p>
                </div>
                <Screenshot name={s.image} />
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Как устроено */}
      <section id="how" className="scroll-mt-20 border-y border-line bg-surface">
        <div className="mx-auto max-w-[1180px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
            {t.how.title}
          </h2>
          <ul className="mt-8 grid gap-x-10 gap-y-8 sm:grid-cols-2 lg:grid-cols-3">
            {t.how.items.map((c) => (
              <li key={c.title} className="flex gap-3">
                <span className="mt-2 inline-block size-2.5 shrink-0 rounded-full bg-accent" aria-hidden />
                <div>
                  <h3 className="text-[16px] font-semibold text-ink">{c.title}</h3>
                  <p className="mt-1.5 text-[14px] leading-5 text-ink-2">{c.text}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Тарифы */}
      <section id="plans" className="scroll-mt-20">
        <div className="mx-auto max-w-[1180px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
            {t.plans.title}
          </h2>
          <p className="mt-2 max-w-[720px] text-[14px] leading-5 text-ink-2">{t.plans.text}</p>
          {plans.isPending ? null : plans.data?.length ? (
            <ul className="mt-8 grid gap-4 md:grid-cols-3">
              {plans.data.map((p) => {
                const limits: string[] = [];
                limits.push(p.limits.users != null ? t.plans.users(p.limits.users) : t.plans.usersUnlimited);
                if (p.limits.projects != null) limits.push(t.plans.projects(p.limits.projects));
                if (p.limits.files_mb != null) limits.push(t.plans.files(p.limits.files_mb));
                // Пустой список — тариф без дополнительных разделов (только основа); полный — все разделы
                const modulesText =
                  p.modules.length === 0
                    ? t.plans.coreOnly
                    : p.modules.length >= PLAN_MODULES.length
                      ? t.plans.allModules
                      : lang === 'ru'
                        ? p.modules.map(moduleTitle).join(', ')
                        : t.plans.modulesCount(p.modules.length);
                const price =
                  p.priceMonth == null && p.priceYear == null
                    ? null
                    : [
                        p.priceMonth != null
                          ? `${money(p.priceMonth, p.currency, lang)} ${t.plans.perMonth}`
                          : null,
                        p.priceYear != null
                          ? `${money(p.priceYear, p.currency, lang)} ${t.plans.perYear}`
                          : null,
                      ].filter(Boolean);
                return (
                  <li key={p.code} className="flex flex-col rounded-lg border border-line bg-surface p-6">
                    <h3 className="text-[20px] font-semibold text-ink" lang={ru}>
                      {p.name}
                    </h3>
                    {p.description && lang === 'ru' ? (
                      <p className="mt-1.5 text-[14px] leading-5 text-ink-2">{p.description}</p>
                    ) : null}
                    <p className="num mt-4 text-[17px] font-semibold text-ink">
                      {price ? price.join(' · ') : t.plans.byAgreement}
                    </p>
                    <p className="mt-3 text-[14px] leading-5 text-ink-2">{limits.join(' · ')}</p>
                    <p className="mt-3 text-[13px] leading-5 text-ink-2">
                      <span className="font-medium text-ink">{t.plans.modules}: </span>
                      {modulesText}
                    </p>
                    {guest ? (
                      <Link
                        to="/request-access"
                        className={clsx(btnSecondary, 'mt-6 h-11 w-full text-[14px]')}
                      >
                        {t.actions.request}
                      </Link>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="mt-6 text-[14px] text-ink-2">{t.plans.empty}</p>
          )}
        </div>
      </section>

      {/* Частые вопросы */}
      <section id="faq" className="scroll-mt-20 border-y border-line bg-surface">
        <div className="mx-auto max-w-[860px] px-4 py-14 md:px-6 md:py-16">
          <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
            {t.faq.title}
          </h2>
          <div className="mt-6 divide-y divide-line border-y border-line">
            {t.faq.items.map((f) => (
              <details key={f.q} className="group">
                <summary className="flex min-h-12 cursor-pointer list-none items-center justify-between gap-4 py-3 text-[16px] font-medium text-ink [&::-webkit-details-marker]:hidden">
                  {f.q}
                  <ArrowRight
                    className="size-4 shrink-0 text-ink-2 transition-transform group-open:rotate-90"
                    aria-hidden
                  />
                </summary>
                <p className="pb-4 text-[14px] leading-6 text-ink-2">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Самостоятельная регистрация (ГЛ-05): только если включена на площадке */}
      {showSignup ? (
        <section id="signup" className="scroll-mt-20 border-b border-line">
          <div className="mx-auto grid max-w-[1180px] gap-8 px-4 py-14 md:px-6 md:py-16 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
            <div>
              <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
                {t.signup.title}
              </h2>
              <p className="mt-2 text-[15px] leading-6 text-ink-2">{t.signup.text}</p>
              <Link
                to="/request-access"
                className="mt-4 inline-flex min-h-11 items-center text-[14px] font-medium text-ink underline underline-offset-4"
              >
                {t.signup.orRequest}
              </Link>
            </div>
            <SignupForm lang={lang} t={t.signup} />
          </div>
        </section>
      ) : null}

      {/* Контакты */}
      <section id="contacts" className="scroll-mt-20">
        <div className="mx-auto grid max-w-[1180px] gap-8 px-4 py-14 md:grid-cols-2 md:px-6 md:py-16">
          <div>
            <h2 className="text-[26px] leading-8 font-semibold tracking-[-0.01em] md:text-[30px] md:leading-9">
              {t.contacts.title}
            </h2>
            <p className="mt-2 max-w-[520px] text-[15px] leading-6 text-ink-2">
              {site.isPending ? '' : contact ? t.contacts.text : t.contacts.noContact}
            </p>
            {contact && contactHref ? (
              <a
                href={contactHref}
                className="mt-4 inline-flex min-h-11 items-center gap-2 text-[17px] font-semibold text-ink underline-offset-4 hover:underline"
              >
                {contactIsMail ? (
                  <Mail className="size-5" aria-hidden />
                ) : (
                  <Phone className="size-5" aria-hidden />
                )}
                {contact}
              </a>
            ) : null}
          </div>
          <div className="flex flex-col items-start gap-3 md:items-end">
            {guest ? (
              <Link to="/request-access" className={btnPrimary}>
                {t.actions.request}
              </Link>
            ) : (
              <Link to="/" className={btnPrimary}>
                {t.actions.toPortal}
              </Link>
            )}
            {guest && demoEnabled ? (
              <>
                <p className="text-[13px] text-ink-2">{t.contacts.demoHint}</p>
                <DemoEntry label={t.actions.demo} variant="secondary" size="lg" hint={false} />
              </>
            ) : null}
          </div>
        </div>
      </section>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto flex max-w-[1180px] flex-wrap items-center justify-between gap-4 px-4 py-8 md:px-6">
          <div>
            <Logo height={20} className="text-ink" />
            <p className="mt-3 text-[13px] text-ink-2">{t.footer.line}</p>
            <p className="text-[12px] text-ink-2">{t.footer.made}</p>
          </div>
          <LangSwitch lang={lang} onChange={pick} />
        </div>
      </footer>
      <CookieBanner t={t.cookie} />
    </div>
  );
}
